import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ConsultationStatusDefinition } from '../types/Consultation';
import type { ConsultationContextChip } from './ConsultationContextHeader';
import styles from './ConsultationRecordShell.module.css';

export type ConsultationCoreFieldsFormProps = {
  status: string;
  summary: string;
  notes: string;
  availableStatuses: ConsultationStatusDefinition[];
  disabled?: boolean;
  layout?: 'default' | 'sidebar' | 'fullWidth';
  metadataChips?: ConsultationContextChip[];
  onStatusChange: (value: string) => void;
  onSummaryChange: (value: string) => void;
  onNotesChange: (value: string) => void;
};

export const ConsultationCoreFieldsForm: React.FC<ConsultationCoreFieldsFormProps> = ({
  status,
  summary,
  notes,
  availableStatuses,
  disabled = false,
  layout = 'default',
  metadataChips = [],
  onStatusChange,
  onSummaryChange,
  onNotesChange,
}) => {
  const { t } = useTranslation(['consultations', 'common']);

  return (
    <section
      className={[
        styles.panel,
        layout === 'sidebar' ? styles.coreFieldsSidebar : '',
        layout === 'fullWidth' ? styles.coreFieldsFullWidth : '',
      ].filter(Boolean).join(' ')}
    >
      <h2 className={styles.sectionTitle}>{t('consultationCoreFields', { defaultValue: 'Consultation details' })}</h2>
      <div className={styles.formGrid}>
        <div className={styles.fieldRow}>
          <label className={styles.fieldLabel}>{t('status', { defaultValue: 'Status' })}</label>
          <select className="text-input" value={status} onChange={(event) => onStatusChange(event.target.value)} disabled={disabled}>
            <option value="">{t('selectStatus', { defaultValue: 'Select status' })}</option>
            {availableStatuses.map((item) => (
              <option key={item.id} value={item.name}>{item.name}</option>
            ))}
          </select>
        </div>
        <div className={`${styles.fieldRow} ${styles.fieldRowFull}`}>
          <label className={styles.fieldLabel}>{t('summary', { defaultValue: 'Summary' })}</label>
          <textarea className="text-input" rows={3} value={summary} onChange={(event) => onSummaryChange(event.target.value)} disabled={disabled} />
        </div>
        <div className={`${styles.fieldRow} ${styles.fieldRowFull}`}>
          <label className={styles.fieldLabel}>{t('notes', { defaultValue: 'Notes' })}</label>
          <textarea className="text-input" rows={4} value={notes} onChange={(event) => onNotesChange(event.target.value)} disabled={disabled} />
        </div>
      </div>
      {metadataChips.length > 0 ? (
        <div className={styles.coreFieldsMetadata}>
          {metadataChips.map((chip) => (
            <div key={chip.label} className={styles.contextChip}>
              <span className={styles.chipLabel}>{chip.label}</span>
              <span className={styles.chipValue}>{chip.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};
